/**
 * The arithmetic behind the answer overview, kept out of the component so it
 * can be tested without a DOM.
 *
 * The overview exists because scoring reaches a question only through a
 * capability: `eval_input` derives its question set from capabilities and
 * nothing else. An answer to a question no capability claims is therefore
 * invisible in every report, and the admin has no way to notice. Here every
 * question is bound to the topic that lists it instead, so no recorded answer
 * can go missing from the page.
 *
 * These numbers are deliberately not report numbers. A topic average here is
 * the unweighted mean of its answered, scorable questions; it ignores capability
 * weights and topic relevancy because a topic-bound question has neither. It is
 * a comparison aid, not a score, and the page says so.
 */
import type {
  Answer,
  Product,
  Question,
  QuestionCoverage,
  TreeAnswer,
  TreeQuestion,
  TreeTopic,
} from "../../api/domain.ts";

/** One question as the matrix shows it, with the answer that counts per product. */
export type OverviewQuestion = {
  id: string;
  title: string;
  /** Context-only questions carry text, never a value, and never average in. */
  informational: boolean;
  /**
   * What a report on the chosen view would do with this question's answers.
   * Absent for questions no topic lists: the tree endpoint never sees them, so
   * there is no coverage verdict to report.
   */
  coverage: QuestionCoverage | undefined;
  /** The resolved answer by product id; a missing key means no answer. */
  answers: Record<string, TreeAnswer>;
  /**
   * How many answers are on file per product for a question outside the tree.
   * Only a count: no precedence has been applied to these, so naming one of
   * them "the" answer would be a guess. The fix is to give the question a topic.
   */
  recorded: Record<string, number>;
};

/** One topic's questions. Every topic is a group of its own, so nothing repeats. */
export type OverviewGroup = {
  /** The topic, or absent for the group of questions no topic lists. */
  topic: string | undefined;
  /** The topic's title, so a reader can place it; empty for the loose group. */
  path: string[];
  questions: OverviewQuestion[];
};

/** A topic's standing for one product, with the thinness of it on show. */
export type GroupAverage = {
  /** Absent when the product answered none of the scorable questions. */
  mean: number | undefined;
  answered: number;
  /** The scorable questions in the group; informational ones are excluded. */
  total: number;
};

const isInformational = (question: Pick<Question, "answer_type">): boolean =>
  question.answer_type.kind === "text";

const fromTree = (question: TreeQuestion): OverviewQuestion => ({
  id: question.id,
  title: question.title,
  informational: question.informational,
  coverage: question.coverage,
  answers: Object.fromEntries(question.answers.map((answer) => [answer.product, answer])),
  recorded: {},
});

/**
 * Every topic that lists at least one question, in catalog order. An empty topic
 * contributes no row, so it is left out rather than shown blank.
 */
export const overviewGroups = (topics: readonly TreeTopic[]): OverviewGroup[] =>
  topics
    .filter((topic) => topic.questions.length > 0)
    .map((topic) => ({
      topic: topic.id,
      path: [topic.title],
      questions: topic.questions.map(fromTree),
    }));

/**
 * The questions no topic lists, if there are any. They cannot appear in the
 * tree at all, so they are gathered from the flat catalog and paired with a
 * count of the answers on file, which is the part that is certainly true.
 */
export const unassignedGroup = (
  questions: readonly Question[],
  assigned: ReadonlySet<string>,
  answers: readonly Answer[],
): OverviewGroup | undefined => {
  const orphans = questions.filter((question) => !assigned.has(question.id));
  if (orphans.length === 0) return undefined;

  const wanted = new Set(orphans.map((question) => question.id));
  const counts = new Map<string, Record<string, number>>();
  for (const answer of answers) {
    if (!wanted.has(answer.question)) continue;
    const perProduct = counts.get(answer.question) ?? {};
    perProduct[answer.product] = (perProduct[answer.product] ?? 0) + 1;
    counts.set(answer.question, perProduct);
  }

  return {
    topic: undefined,
    path: [],
    questions: orphans.map((question) => ({
      id: question.id,
      title: question.title,
      informational: isInformational(question),
      coverage: undefined,
      answers: {},
      recorded: counts.get(question.id) ?? {},
    })),
  };
};

/**
 * The unweighted mean over the questions this product actually answered.
 * Unanswered questions are left out rather than counted as zero, because a gap
 * in the data is not a weak product; `answered` beside the mean is what makes a
 * thin average visible as thin.
 */
export const groupAverage = (group: OverviewGroup, product: string): GroupAverage => {
  const scorable = group.questions.filter((question) => !question.informational);
  const values = scorable
    .map((question) => question.answers[product]?.value)
    .filter((value): value is number => typeof value === "number");

  return {
    mean:
      values.length === 0
        ? undefined
        : values.reduce((sum, value) => sum + value, 0) / values.length,
    answered: values.length,
    total: scorable.length,
  };
};

/** Whether any product answered anything in the group, which is what makes it worth a row. */
export const hasAnswers = (group: OverviewGroup): boolean =>
  group.questions.some(
    (question) =>
      Object.keys(question.answers).length > 0 || Object.keys(question.recorded).length > 0,
  );

/** One product's standing on a single question, answered or not. */
export type ComparisonRow = {
  product: string;
  name: string;
  /** The answer that counts for this product; absent when it has none. */
  answer: TreeAnswer | undefined;
};

/** One question read across every product, which is the matrix turned on its side. */
export type AnswerComparison = {
  /** A row per product, in the order the products were handed in. */
  rows: ComparisonRow[];
  /**
   * The plain mean of the values on file. Absent for an informational question
   * and for one nobody scored; like every number here it is a comparison aid,
   * not a report value.
   */
  mean: number | undefined;
  /** Products with an answer, and products in total, so a thin row set reads as thin. */
  answered: number;
  total: number;
};

/**
 * One question against the products it was asked about. The products decide the
 * rows, not the answers: a product that answered nothing is the point of the
 * dialog, so it has to hold a row rather than be left out of it.
 */
export const answerComparison = (
  question: Pick<TreeQuestion, "answers" | "informational">,
  products: readonly Product[],
): AnswerComparison => {
  const byProduct = new Map(question.answers.map((answer) => [answer.product, answer]));
  const rows = products.map((product) => ({
    product: product.id,
    name: product.name,
    answer: byProduct.get(product.id),
  }));
  const values = question.informational
    ? []
    : rows
        .map((row) => row.answer?.value)
        .filter((value): value is number => typeof value === "number");

  return {
    rows,
    mean:
      values.length === 0
        ? undefined
        : values.reduce((sum, value) => sum + value, 0) / values.length,
    answered: rows.filter((row) => row.answer !== undefined).length,
    total: rows.length,
  };
};
